import Dexie, { type EntityTable, type Table } from 'dexie';
import { defaultShiftTypes, localProfileSchema, localShiftSchema, localShiftTypeSchema, wageTermSchema, workplaceSchema, type LocalProfile, type LocalShift, type LocalShiftType, type WageTerm, type Workplace, type TaxPreset } from '@wolhasudang/shared';
import { z } from 'zod';
import { termFor } from '@wolhasudang/engine';

export interface SettingRecord { key: string; value: unknown }
export interface CustomAllowanceRecord {
  id: string;
  name: string;
  amount: number;
  mode: 'perNight' | 'monthly' | 'perShiftType';
  shiftTypeId?: string;
  legalCoverage: 'supplemental' | 'includesNight' | 'includesOvertime' | 'includesHoliday' | 'unknown';
  coverageConfirmed: boolean;
}
export interface PaycheckItemRecord { id: string; label: string; canonicalKind: string; amount: number; scopeConfirmed: boolean }
export interface PaycheckRecord { month: string; items: PaycheckItemRecord[]; fixedPayComplete: boolean; deductionsComplete: boolean; actualDeposit?: number; reconciledAt?: string }
export interface SnapshotRecord { id: string; createdAt: number; payload: string; reason: 'auto-monthly' | 'pre-restore' | 'manual' }
export interface OutboxEventRecord { id?: number; createdAt: number; payload: unknown }
export interface SchemaMetaRecord { key: 'schema'; version: 2 | 3 | 4; migratedAt: number; migrationError?: string }

export class WolhasudangDb extends Dexie {
  profiles!: EntityTable<LocalProfile, 'id'>;
  shiftTypes!: EntityTable<LocalShiftType, 'id'>;
  shifts!: Table<LocalShift, [string, string]>;
  customAllowances!: EntityTable<CustomAllowanceRecord, 'id'>;
  paychecks!: EntityTable<PaycheckRecord, 'month'>;
  snapshots!: EntityTable<SnapshotRecord, 'id'>;
  settings!: EntityTable<SettingRecord, 'key'>;
  outboxEvents!: EntityTable<OutboxEventRecord, 'id'>;
  schemaMeta!: EntityTable<SchemaMetaRecord, 'key'>;
  workplaces!: EntityTable<Workplace, 'id'>;
  wageTerms!: EntityTable<WageTerm, 'id'>;

  constructor(name = 'wolhasudang') {
    super(name);
    this.version(1).stores({
      profiles: 'id', shiftTypes: 'id, sortOrder', shifts: 'date, shiftTypeId',
      customAllowances: 'id', paychecks: 'month', snapshots: 'id, createdAt',
      settings: 'key', outboxEvents: '++id, createdAt',
    });
    this.version(2).stores({
      profiles: 'id', shiftTypes: 'id, sortOrder', shifts: 'date, shiftTypeId',
      customAllowances: 'id', paychecks: 'month', snapshots: 'id, createdAt',
    settings: 'key', outboxEvents: '++id, createdAt', schemaMeta: 'key',
    }).upgrade(async (transaction) => {
      await transaction.table<SchemaMetaRecord, string>('schemaMeta').put({ key: 'schema', version: 2, migratedAt: Date.now() });
    });
    this.version(3).stores({
      profiles: 'id', shiftTypes: 'id, sortOrder', shifts: 'date, shiftTypeId, workplaceId',
      customAllowances: 'id', paychecks: 'month', snapshots: 'id, createdAt', settings: 'key', outboxEvents: '++id, createdAt', schemaMeta: 'key',
      workplaces: 'id, segment, archivedAt', wageTerms: 'id, workplaceId, effectiveFrom',
    }).upgrade(async (transaction) => {
      const now = new Date().toISOString();
      const profiles = await transaction.table<LocalProfile, string>('profiles').toArray();
      const shifts = await transaction.table<LocalShift, string>('shifts').toArray();
      const shiftTypes = await transaction.table<LocalShiftType, string>('shiftTypes').toArray();
      const customAllowances = await transaction.table<CustomAllowanceRecord, string>('customAllowances').toArray();
      const paychecks = await transaction.table<PaycheckRecord, string>('paychecks').toArray();
      const workplaces = transaction.table<Workplace, string>('workplaces');
      const wageTerms = transaction.table<WageTerm, string>('wageTerms');
      let workplace = (await workplaces.toArray())[0];
      const profile = profiles[0];
      await transaction.table<SnapshotRecord, string>('snapshots').put({
        id: `snap_pre_v3_${Date.now()}`, createdAt: Date.now(), reason: 'pre-restore',
        payload: `raw:${JSON.stringify({ schemaVersion: 1, exportedAt: now, profiles, shiftTypes, shifts, customAllowances, paychecks })}`,
      });
      if (!workplace) {
        const scale = profile?.sector === 'private' ? profile.firmSize : 'unknown';
        workplace = {
          id: 'workplace_default', name: '내 직장', colorToken: 'blue',
          scale: scale === 'under5' || scale === 'fivePlus' ? scale : 'unknown',
          scaleConfirmedAt: profile?.sector === 'private' && profile.establishmentConfirmed ? now : null,
          segment: profile?.sector === 'private' && profile.segment === 'hourly' ? 'hourly' : 'shift',
          archivedAt: null, createdAt: now,
        };
        await workplaces.put(workplace);
      }
      if (profile?.sector === 'private' && profile.ordinaryHourlyRate && !(await wageTerms.count())) {
        const firstDate = shifts.map((shift) => shift.date).sort()[0] ?? now.slice(0, 10);
        await wageTerms.put({
          id: 'wage_term_default', workplaceId: workplace.id, effectiveFrom: firstDate,
          ordinaryHourlyRate: profile.ordinaryHourlyRate, scheduledDaysPerWeek: 0, weeklyScheduledMinutes: 0,
          taxPreset: 'fourInsurance' as TaxPreset, confirmedAt: now, createdAt: now,
        });
      }
      const updatedShifts = shifts.filter((shift) => !shift.workplaceId).map((shift) => ({ ...shift, workplaceId: workplace!.id }));
      if (updatedShifts.length) await transaction.table<LocalShift, string>('shifts').bulkPut(updatedShifts);
      await transaction.table<SchemaMetaRecord, string>('schemaMeta').put({ key: 'schema', version: 3, migratedAt: Date.now() });
    });
    this.version(4).stores({
      profiles: 'id', shiftTypes: 'id, sortOrder', shifts: 'date, shiftTypeId, workplaceId', shiftEntries: '[date+workplaceId], date, shiftTypeId, workplaceId',
      customAllowances: 'id', paychecks: 'month', snapshots: 'id, createdAt', settings: 'key', outboxEvents: '++id, createdAt', schemaMeta: 'key',
      workplaces: 'id, segment, archivedAt', wageTerms: 'id, workplaceId, effectiveFrom',
    }).upgrade(async (transaction) => {
      const now = new Date().toISOString();
      const workplaces = transaction.table<Workplace, string>('workplaces');
      let workplace = (await workplaces.toArray())[0];
      if (!workplace) {
        const now = new Date().toISOString();
        workplace = { id: 'workplace_default', name: '내 직장', colorToken: 'blue', scale: 'unknown', scaleConfirmedAt: null, segment: 'shift', archivedAt: null, createdAt: now };
        await workplaces.put(workplace);
      }
      const legacyShifts = transaction.table<LocalShift, string>('shifts');
      const shiftEntries = transaction.table<LocalShift, [string, string]>('shiftEntries');
      const legacyRecords = await legacyShifts.toArray();
      const preMigration = {
        schemaVersion: 2, exportedAt: now,
        profiles: await transaction.table<LocalProfile, string>('profiles').toArray(),
        shiftTypes: await transaction.table<LocalShiftType, string>('shiftTypes').toArray(),
        shifts: legacyRecords, customAllowances: await transaction.table<CustomAllowanceRecord, string>('customAllowances').toArray(),
        paychecks: await transaction.table<PaycheckRecord, string>('paychecks').toArray(), workplaces: await workplaces.toArray(), wageTerms: await transaction.table<WageTerm, string>('wageTerms').toArray(),
      };
      await transaction.table<SnapshotRecord, string>('snapshots').put({ id: `snap_pre_v4_${Date.now()}`, createdAt: Date.now(), reason: 'pre-restore', payload: `raw:${JSON.stringify(preMigration)}` });
      const records = legacyRecords.map((shift) => ({ ...shift, workplaceId: shift.workplaceId ?? workplace!.id }));
      if (records.length) await shiftEntries.bulkPut(records);
      await legacyShifts.clear();
      await transaction.table<SchemaMetaRecord, string>('schemaMeta').put({ key: 'schema', version: 4, migratedAt: Date.now() });
    });
    this.on('ready', () => {
      this.shifts = this.table<LocalShift, [string, string]>('shiftEntries');
    });
  }
}

export const db = new WolhasudangDb();

const customAllowanceSchema = z.object({
  id: z.string().min(1).max(80), name: z.string().trim().min(1).max(30), amount: z.number().int().nonnegative().max(100_000_000),
  mode: z.enum(['perNight', 'monthly', 'perShiftType']), shiftTypeId: z.string().min(1).max(80).optional(),
  legalCoverage: z.enum(['supplemental', 'includesNight', 'includesOvertime', 'includesHoliday', 'unknown']), coverageConfirmed: z.boolean(),
}).superRefine((value, context) => {
  if (value.mode === 'perShiftType' && !value.shiftTypeId) context.addIssue({ code: 'custom', message: 'shiftTypeId required for perShiftType' });
});
const paycheckSchema = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/),
  items: z.array(z.object({ id: z.string().min(1).max(80), label: z.string().min(1).max(40), canonicalKind: z.string().min(1).max(40), amount: z.number().int().nonnegative().max(1_000_000_000), scopeConfirmed: z.boolean() })).max(200),
  fixedPayComplete: z.boolean(), deductionsComplete: z.boolean(), actualDeposit: z.number().int().nonnegative().max(1_000_000_000).optional(), reconciledAt: z.iso.datetime().optional(),
});
const exportPayloadSchema = z.object({
  schemaVersion: z.union([z.literal(1), z.literal(2), z.literal(3)]), exportedAt: z.iso.datetime(),
  profiles: z.array(localProfileSchema).max(1), shiftTypes: z.array(localShiftTypeSchema).max(200), shifts: z.array(localShiftSchema).max(20_000),
  customAllowances: z.array(customAllowanceSchema).max(200), paychecks: z.array(paycheckSchema).max(240),
  workplaces: z.array(workplaceSchema).max(100).optional().default([]), wageTerms: z.array(wageTermSchema).max(1000).optional().default([]),
}).strict();
export type ExportPayload = z.infer<typeof exportPayloadSchema>;

export function ledgerRecordToken(value: unknown): string {
  return JSON.stringify(value, (_key, item: unknown) => item && typeof item === 'object' && !Array.isArray(item) ? Object.fromEntries(Object.entries(item).sort(([a], [b]) => a.localeCompare(b))) : item) ?? 'null';
}

/** Amounts copied from a payslip are user originals, not a disposable calculation cache. */
export async function invalidatePaycheckConfirmation(months: string[], target = db): Promise<void> {
  for (const month of new Set(months)) {
    const saved = await target.paychecks.get(month);
    if (!saved) continue;
    const original = { ...saved };
    delete original.reconciledAt;
    await target.paychecks.put({ ...original, items: original.items.map((item) => ({ ...item, scopeConfirmed: false })) });
  }
}

export async function savePaycheck(record: PaycheckRecord, target = db, expected?: PaycheckRecord | null): Promise<void> {
  await target.transaction('rw', target.paychecks, async () => {
    if (expected !== undefined && ledgerRecordToken((await target.paychecks.get(record.month)) ?? null) !== ledgerRecordToken(expected)) throw new Error('PAYCHECK_CHANGED');
    await target.paychecks.put(cleanPaycheck(paycheckSchema.parse(record)));
  });
}

function cleanPaycheck({ reconciledAt, actualDeposit, ...value }: z.infer<typeof paycheckSchema>): PaycheckRecord {
  return { ...value, ...(actualDeposit === undefined ? {} : { actualDeposit }), ...(reconciledAt === undefined ? {} : { reconciledAt }) };
}

export async function initializeLocalData(target = db): Promise<void> {
  await target.transaction('rw', [target.shiftTypes, target.schemaMeta], async () => {
    if (await target.shiftTypes.count() === 0) await target.shiftTypes.bulkAdd(defaultShiftTypes);
    await target.schemaMeta.put({ key: 'schema', version: 4, migratedAt: (await target.schemaMeta.get('schema'))?.migratedAt ?? Date.now() });
  });
}

export async function saveWorkplace(workplace: Workplace, target = db): Promise<void> {
  await target.workplaces.put(workplaceSchema.parse(workplace));
}

export async function saveWageTerm(term: WageTerm, target = db): Promise<void> {
  const parsed = wageTermSchema.parse(term);
  const duplicate = await target.wageTerms.where('workplaceId').equals(parsed.workplaceId).filter((value) => value.effectiveFrom === parsed.effectiveFrom).first();
  await target.wageTerms.put(duplicate ? { ...parsed, id: duplicate.id } : parsed);
}

export async function saveFirstJobSetup(input: { profile: LocalProfile; workplace: Workplace; wageTerm: WageTerm; shiftType: LocalShiftType }, target = db): Promise<void> {
  const profile = localProfileSchema.parse(input.profile);
  const workplace = workplaceSchema.parse(input.workplace);
  const wageTerm = wageTermSchema.parse(input.wageTerm);
  const shiftType = localShiftTypeSchema.parse(input.shiftType);
  await target.transaction('rw', [target.profiles, target.workplaces, target.wageTerms, target.shiftTypes], async () => {
    await target.profiles.put(profile);
    await target.workplaces.put(workplace);
    const duplicate = await target.wageTerms.where('workplaceId').equals(wageTerm.workplaceId).filter((value) => value.effectiveFrom === wageTerm.effectiveFrom).first();
    await target.wageTerms.put(duplicate ? { ...wageTerm, id: duplicate.id } : wageTerm);
    await target.shiftTypes.put(shiftType);
  });
}

export async function saveFirstJobShift(input: { shift: LocalShift; shiftType: LocalShiftType; expected?: LocalShift | null }, target = db): Promise<LocalShift | undefined> {
  const shift = localShiftSchema.parse(input.shift);
  const shiftType = localShiftTypeSchema.parse(input.shiftType);
  const workplaceId = shift.workplaceId ?? 'workplace_default';
  return await target.transaction('rw', [target.shifts, target.shiftTypes, target.paychecks], async () => {
    const previous = await target.shifts.get([shift.date, workplaceId]);
    if (input.expected !== undefined && ledgerRecordToken(previous ?? null) !== ledgerRecordToken(input.expected)) throw new Error('SHIFT_CHANGED');
    if (!await target.shiftTypes.get(shiftType.id)) await target.shiftTypes.put(shiftType);
    await target.shifts.put({ ...shift, workplaceId });
    await invalidatePaycheckConfirmation([shift.date.slice(0, 7)], target);
    return previous;
  });
}

export async function ensureDefaultWorkplace(profile: LocalProfile, target = db): Promise<Workplace | undefined> {
  if (profile.sector !== 'private') return undefined;
  const existing = await target.workplaces.toCollection().first();
  if (existing) return existing;
  const now = new Date().toISOString();
  const workplace: Workplace = {
    id: profile.workplaceId ?? 'workplace_default', name: '내 직장', colorToken: 'blue',
    scale: profile.firmSize, scaleConfirmedAt: profile.establishmentConfirmed && profile.firmSize !== 'unknown' ? now : null,
    segment: profile.segment ?? 'shift', archivedAt: null, createdAt: now,
  };
  await saveWorkplace(workplace, target);
  if (profile.ordinaryHourlyRate) {
    const firstShift = await target.shifts.orderBy('date').first();
    await saveWageTerm({ id: `wage_term_${crypto.randomUUID()}`, workplaceId: workplace.id, effectiveFrom: firstShift?.date ?? now.slice(0, 10), ordinaryHourlyRate: profile.ordinaryHourlyRate, scheduledDaysPerWeek: 0, weeklyScheduledMinutes: 0, taxPreset: 'fourInsurance', confirmedAt: now, createdAt: now }, target);
  }
  return workplace;
}

export interface LocalDataHealth {
  ok: boolean;
  schemaVersion: number;
  invalidRecords: string[];
  brokenShiftReferences: string[];
}

export async function inspectLocalData(target = db): Promise<LocalDataHealth> {
  await target.open();
  const [profiles, shiftTypes, shifts, allowances, paychecks, workplaces, wageTerms] = await Promise.all([
    target.profiles.toArray(), target.shiftTypes.toArray(), target.shifts.toArray(),
    target.customAllowances.toArray(), target.paychecks.toArray(), target.workplaces.toArray(), target.wageTerms.toArray(),
  ]);
  const invalidRecords = [
    ...profiles.flatMap((value) => localProfileSchema.safeParse(value).success ? [] : [`profile:${String((value as { id?: unknown }).id ?? 'unknown')}`]),
    ...shiftTypes.flatMap((value) => localShiftTypeSchema.safeParse(value).success ? [] : [`shiftType:${String((value as { id?: unknown }).id ?? 'unknown')}`]),
    ...shifts.flatMap((value) => localShiftSchema.safeParse(value).success ? [] : [`shift:${String((value as { date?: unknown }).date ?? 'unknown')}`]),
    ...allowances.flatMap((value) => customAllowanceSchema.safeParse(value).success ? [] : [`allowance:${String((value as { id?: unknown }).id ?? 'unknown')}`]),
    ...paychecks.flatMap((value) => paycheckSchema.safeParse(value).success ? [] : [`paycheck:${String((value as { month?: unknown }).month ?? 'unknown')}`]),
    ...workplaces.flatMap((value) => workplaceSchema.safeParse(value).success ? [] : [`workplace:${String((value as { id?: unknown }).id ?? 'unknown')}`]),
    ...wageTerms.flatMap((value) => wageTermSchema.safeParse(value).success ? [] : [`wageTerm:${String((value as { id?: unknown }).id ?? 'unknown')}`]),
  ];
  const typeIds = new Set(shiftTypes.map((value) => value.id));
  const brokenShiftReferences = shifts.filter((value) => !typeIds.has(value.shiftTypeId)).map((value) => value.date);
  return { ok: invalidRecords.length === 0 && brokenShiftReferences.length === 0, schemaVersion: (await target.schemaMeta.get('schema'))?.version ?? target.verno, invalidRecords, brokenShiftReferences };
}

export async function saveProfile(profile: LocalProfile, target = db): Promise<void> {
  await target.profiles.put(localProfileSchema.parse(profile));
  await ensureDefaultWorkplace(profile, target);
}

export async function saveShiftType(shiftType: LocalShiftType, target = db): Promise<void> {
  await target.shiftTypes.put(localShiftTypeSchema.parse(shiftType));
}

export async function deleteShiftType(id: string, target = db): Promise<void> {
  if (await target.shifts.where('shiftTypeId').equals(id).count()) throw new Error('SHIFT_TYPE_IN_USE');
  await target.shiftTypes.delete(id);
}

export async function toggleShift(date: string, shiftTypeId: string, target = db, workplaceId?: string): Promise<'saved' | 'deleted'> {
  return (await toggleShiftWithSnapshot(date, shiftTypeId, target, workplaceId)).action;
}

export async function toggleShiftWithSnapshot(date: string, shiftTypeId: string, target = db, workplaceId?: string): Promise<{ action: 'saved' | 'deleted'; previous: LocalShift[]; expected: LocalShift[] }> {
  return await target.transaction('rw', [target.shifts, target.paychecks, target.workplaces], async () => {
    const firstWorkplace = await target.workplaces.toCollection().first();
    const selectedWorkplaceId = workplaceId ?? firstWorkplace?.id ?? 'workplace_default';
    const existing = await target.shifts.where('date').equals(date).filter((shift) => shift.workplaceId === selectedWorkplaceId).first();
    if (existing?.shiftTypeId === shiftTypeId) {
      await target.shifts.delete([date, selectedWorkplaceId]);
      await invalidatePaycheckConfirmation([date.slice(0, 7)], target);
      return { action: 'deleted', previous: [existing], expected: [] };
    }
    await target.shifts.put(localShiftSchema.parse({ date, shiftTypeId, actualConfirmed: false, workplaceId: selectedWorkplaceId }));
    await invalidatePaycheckConfirmation([date.slice(0, 7)], target);
    return { action: 'saved', previous: existing ? [existing] : [], expected: [(await target.shifts.get([date, selectedWorkplaceId]))!] };
  });
}

export async function applyPattern(month: string, pattern: string[], target = db, workplaceId?: string): Promise<{ dates: string[]; previous: LocalShift[]; expected: LocalShift[] }> {
  if (!/^\d{4}-\d{2}$/.test(month) || pattern.length === 0) throw new Error('INVALID_PATTERN');
  const [year = 0, rawMonth = 0] = month.split('-').map(Number);
  const dayCount = new Date(year, rawMonth, 0).getDate();
  const dates = Array.from({ length: dayCount }, (_, index) => `${month}-${String(index + 1).padStart(2, '0')}`);
  return target.transaction('rw', [target.shifts, target.paychecks, target.workplaces], async () => {
    const firstWorkplace = await target.workplaces.toCollection().first();
    const selectedWorkplaceId = workplaceId ?? firstWorkplace?.id ?? 'workplace_default';
    const previous = await target.shifts.where('date').between(dates[0]!, dates.at(-1)!, true, true).filter((shift) => shift.workplaceId === selectedWorkplaceId).toArray();
    const expected = dates.map((date, index) => localShiftSchema.parse({ date, shiftTypeId: pattern[index % pattern.length], actualConfirmed: false, workplaceId: selectedWorkplaceId }));
    if (previous.length) await target.shifts.bulkDelete(previous.map((shift) => [shift.date, selectedWorkplaceId] as [string, string]));
    await target.shifts.bulkPut(expected);
    await invalidatePaycheckConfirmation([month], target);
    return { dates, previous, expected };
  });
}

export async function restoreShiftSnapshot(dates: string[], previous: LocalShift[], target = db, workplaceId?: string, expected?: LocalShift[]): Promise<void> {
  await target.transaction('rw', [target.shifts, target.paychecks], async () => {
    const current = await target.shifts.where('date').anyOf(dates).filter((shift) => workplaceId === undefined || shift.workplaceId === workplaceId).toArray();
    const fingerprint = (rows: LocalShift[]) => JSON.stringify(rows.map(ledgerRecordToken).sort());
    if (expected && fingerprint(current) !== fingerprint(expected)) throw new Error('SHIFT_CHANGED');
    if (current.length) await target.shifts.bulkDelete(current.map((shift) => [shift.date, shift.workplaceId ?? workplaceId ?? 'workplace_default'] as [string, string]));
    const restored = previous.filter((shift) => workplaceId === undefined || shift.workplaceId === workplaceId).map((shift) => localShiftSchema.parse({ ...shift, workplaceId: shift.workplaceId ?? workplaceId ?? 'workplace_default' }));
    if (restored.length) await target.shifts.bulkPut(restored);
    await invalidatePaycheckConfirmation(dates.map((date) => date.slice(0, 7)), target);
  });
}

export async function putRecognizedShifts(shifts: LocalShift[], target = db): Promise<{ previous: LocalShift[]; expected: LocalShift[] }> {
  if (!shifts.length) return { previous: [], expected: [] };
  const workplace = await target.workplaces.toCollection().first();
  const parsed = shifts.map((shift) => localShiftSchema.parse({ ...shift, workplaceId: shift.workplaceId ?? workplace?.id ?? 'workplace_default' }));
  return target.transaction('rw', [target.shifts, target.paychecks], async () => {
    const dates = [...new Set(parsed.map((shift) => shift.date))];
    const previous = await target.shifts.where('date').anyOf(dates).filter((shift) => parsed.some((next) => next.date === shift.date && next.workplaceId === shift.workplaceId)).toArray();
    const keys = previous.map((shift) => [shift.date, shift.workplaceId]);
    if (keys.length) await target.shifts.bulkDelete(keys as [string, string][]);
    await target.shifts.bulkPut(parsed);
    await invalidatePaycheckConfirmation(parsed.map((shift) => shift.date.slice(0, 7)), target);
    return { previous, expected: parsed };
  });
}

export async function exportLocalData(target = db): Promise<string> {
  return target.transaction('r', userTables(target), async () => JSON.stringify(exportPayloadSchema.parse(await readPayload(target)), null, 2));
}

function userTables(target: WolhasudangDb) {
  return [target.profiles, target.shiftTypes, target.shifts, target.customAllowances, target.paychecks, target.workplaces, target.wageTerms];
}

async function readPayload(target: WolhasudangDb) {
  return {
    schemaVersion: 3,
    exportedAt: new Date().toISOString(),
    profiles: await target.profiles.toArray(), shiftTypes: await target.shiftTypes.toArray(), shifts: await target.shifts.toArray(),
    customAllowances: await target.customAllowances.toArray(), paychecks: await target.paychecks.toArray(),
    workplaces: await target.workplaces.toArray(), wageTerms: await target.wageTerms.toArray(),
  };
}

function payloadToken(payload: Awaited<ReturnType<typeof readPayload>>) {
  return JSON.stringify({ ...payload, exportedAt: '' });
}

export async function exportOriginalLocalData(target = db): Promise<string> {
  return target.transaction('r', userTables(target), async () => JSON.stringify(await readPayload(target), null, 2));
}

export async function localImportToken(target = db): Promise<string> {
  return target.transaction('r', userTables(target), async () => payloadToken(await readPayload(target)));
}

export async function localImportPreview(incoming: ExportPayload, target = db): Promise<{ token: string; retained: number; changed: number; mergeBlocked: boolean }> {
  return target.transaction('r', userTables(target), async () => {
    const raw = await readPayload(target); const current = previewLocalImport(JSON.stringify(raw));
    let retained = 0; let changed = 0;
    for (const table of ['profiles', 'shiftTypes', 'shifts', 'customAllowances', 'paychecks', 'workplaces', 'wageTerms'] as const) {
      const key = (value: unknown) => { const row = value as Record<string, unknown>; return table === 'shifts' ? ledgerRecordToken([row.date, row.workplaceId]) : table === 'paychecks' ? String(row.month) : table === 'wageTerms' ? ledgerRecordToken([row.workplaceId, row.effectiveFrom]) : String(row.id); };
      const saved = new Map(current[table].map((row) => [key(row), ledgerRecordToken(row)]));
      for (const row of incoming[table]) if (saved.has(key(row))) { retained++; if (saved.get(key(row)) !== ledgerRecordToken(row)) changed++; }
    }
    let mergeBlocked = false; try { mergePayload(current, incoming); } catch { mergeBlocked = true; }
    return { token: payloadToken(raw), retained, changed, mergeBlocked };
  });
}

export function previewLocalImport(plainJson: string): ExportPayload {
  if (new TextEncoder().encode(plainJson).byteLength > 5 * 1024 * 1024) throw new Error('IMPORT_TOO_LARGE');
  let value: unknown;
  try { value = JSON.parse(plainJson); } catch { throw new Error('INVALID_IMPORT_JSON'); }
  const parsed = exportPayloadSchema.safeParse(value);
  if (!parsed.success) throw new Error('INVALID_IMPORT_SCHEMA');
  return normalizeImport(parsed.data);
}

function normalizeImport(payload: ExportPayload): ExportPayload {
  const normalized = structuredClone(payload);
  // v1 exports and early v2 local ledgers could omit their implicit default workplace.
  if (!normalized.workplaces.length && normalized.shifts.every((shift) => !shift.workplaceId || shift.workplaceId === 'workplace_default') && !normalized.wageTerms.length) {
    normalized.workplaces.push({ id: 'workplace_default', name: '내 직장', colorToken: 'blue', scale: 'unknown', scaleConfirmedAt: null, segment: 'shift', archivedAt: null, createdAt: payload.exportedAt });
  }
  const fallback = normalized.workplaces[0]?.id;
  normalized.shifts = normalized.shifts.map((shift) => ({ ...shift, ...(shift.workplaceId ? {} : fallback ? { workplaceId: fallback } : {}) }));
  const unique = <T,>(rows: T[], key: (row: T) => string) => {
    if (new Set(rows.map(key)).size !== rows.length) throw new Error('IMPORT_DUPLICATE_KEY');
  };
  unique(normalized.profiles, (row) => row.id); unique(normalized.shiftTypes, (row) => row.id);
  unique(normalized.shifts, (row) => JSON.stringify([row.date, row.workplaceId]));
  unique(normalized.customAllowances, (row) => row.id); unique(normalized.paychecks, (row) => row.month);
  unique(normalized.workplaces, (row) => row.id); unique(normalized.wageTerms, (row) => row.id);
  unique(normalized.wageTerms, (row) => JSON.stringify([row.workplaceId, row.effectiveFrom]));
  for (const paycheck of normalized.paychecks) unique(paycheck.items, (row) => row.id);
  const types = new Set(normalized.shiftTypes.map((row) => row.id)); const workplaces = new Set(normalized.workplaces.map((row) => row.id));
  if (normalized.shifts.some((row) => !types.has(row.shiftTypeId) || !row.workplaceId || !workplaces.has(row.workplaceId))
    || normalized.customAllowances.some((row) => row.shiftTypeId && !types.has(row.shiftTypeId))
    || normalized.wageTerms.some((row) => !workplaces.has(row.workplaceId))
    || normalized.profiles.some((row) => row.sector === 'private' && row.workplaceId && !workplaces.has(row.workplaceId))) throw new Error('IMPORT_BROKEN_REFERENCE');
  return normalized;
}

export async function createLocalSnapshot(reason: SnapshotRecord['reason'], target = db): Promise<string> {
  const id = `snap_${crypto.randomUUID()}`;
  const payload = await gzipBase64(await exportLocalData(target));
  await target.snapshots.put({ id, createdAt: Date.now(), payload, reason });
  const stale = await target.snapshots.orderBy('createdAt').reverse().offset(12).primaryKeys();
  if (stale.length) await target.snapshots.bulkDelete(stale);
  return id;
}

export async function importLocalData(plainJson: string, mode: 'replace' | 'merge', target = db, expectedToken?: string): Promise<ExportPayload> {
  const payload = previewLocalImport(plainJson);
  await applyImportPayload(payload, mode, target, expectedToken);
  return payload;
}

function mergePayload(current: ExportPayload, incoming: ExportPayload): ExportPayload {
  const merge = <T,>(a: T[], b: T[], key: (row: T) => string) => [...a, ...b.filter((row) => !a.some((saved) => key(saved) === key(row)))];
  // A new shift must never silently acquire a different schedule from a colliding imported ID.
  for (const type of incoming.shiftTypes) {
    const saved = current.shiftTypes.find((row) => row.id === type.id);
    if (saved && JSON.stringify(saved) !== JSON.stringify(type) && incoming.shifts.some((shift) => shift.shiftTypeId === type.id && !current.shifts.some((row) => row.date === shift.date && row.workplaceId === shift.workplaceId))) throw new Error('IMPORT_REFERENCE_CONFLICT');
  }
  const next = normalizeImport({ ...incoming,
    profiles: merge(current.profiles, incoming.profiles, (row) => row.id), shiftTypes: merge(current.shiftTypes, incoming.shiftTypes, (row) => row.id),
    shifts: merge(current.shifts, incoming.shifts, (row) => JSON.stringify([row.date, row.workplaceId])), customAllowances: merge(current.customAllowances, incoming.customAllowances, (row) => row.id),
    paychecks: merge(current.paychecks, incoming.paychecks, (row) => row.month), workplaces: merge(current.workplaces, incoming.workplaces, (row) => row.id),
    wageTerms: merge(current.wageTerms, incoming.wageTerms, (row) => JSON.stringify([row.workplaceId, row.effectiveFrom])),
  });
  const meaning = (payload: ExportPayload, shift: LocalShift) => {
    const workplace = payload.workplaces.find((row) => row.id === shift.workplaceId);
    const term = termFor(payload.wageTerms, shift.workplaceId ?? '', shift.date);
    return ledgerRecordToken({ workplace: workplace && { name: workplace.name, scale: workplace.scale, confirmed: Boolean(workplace.scaleConfirmedAt), segment: workplace.segment }, term: term && { effectiveFrom: term.effectiveFrom, ordinaryHourlyRate: term.ordinaryHourlyRate, scheduledDaysPerWeek: term.scheduledDaysPerWeek, weeklyScheduledMinutes: term.weeklyScheduledMinutes, taxPreset: term.taxPreset, confirmed: Boolean(term.confirmedAt) } });
  };
  // Adding history must not reinterpret current shifts, and new imported shifts keep their own dated conditions.
  for (const shift of next.shifts) {
    const origin = current.shifts.some((row) => row.date === shift.date && row.workplaceId === shift.workplaceId) ? current : incoming;
    if (meaning(origin, shift) !== meaning(next, shift)) throw new Error('IMPORT_REFERENCE_CONFLICT');
  }
  return next;
}

async function applyImportPayload(payload: ExportPayload, mode: 'replace' | 'merge', target: WolhasudangDb, expectedToken?: string): Promise<void> {
  await target.transaction('rw', [...userTables(target), target.snapshots], async () => {
    const original = await readPayload(target);
    if (expectedToken !== undefined && payloadToken(original) !== expectedToken) throw new Error('IMPORT_PREVIEW_STALE');
    const next = mode === 'merge' ? mergePayload(previewLocalImport(JSON.stringify(original)), payload) : payload;
    // Raw is an existing snapshot format. Keeping it inside this transaction makes the undo source exact.
    await target.snapshots.put({ id: `snap_${crypto.randomUUID()}`, createdAt: Date.now(), reason: 'pre-restore', payload: `raw:${JSON.stringify(original)}` });
    const tables = userTables(target);
    await Promise.all(tables.map((table) => table.clear()));
    await target.profiles.bulkPut(next.profiles); await target.shiftTypes.bulkPut(next.shiftTypes); await target.shifts.bulkPut(next.shifts);
    await target.customAllowances.bulkPut(next.customAllowances.map(({ shiftTypeId, ...value }) => ({ ...value, ...(shiftTypeId === undefined ? {} : { shiftTypeId }) })));
    await target.paychecks.bulkPut(next.paychecks.map(cleanPaycheck));
    await target.workplaces.bulkPut(next.workplaces); await target.wageTerms.bulkPut(next.wageTerms);
    const readBack = previewLocalImport(JSON.stringify(await readPayload(target)));
    for (const key of ['profiles', 'shiftTypes', 'shifts', 'customAllowances', 'paychecks', 'workplaces', 'wageTerms'] as const) {
      const fingerprint = (rows: unknown[]) => JSON.stringify(rows.map((row) => JSON.stringify(row)).sort());
      if (fingerprint(readBack[key]) !== fingerprint(next[key])) throw new Error('IMPORT_READBACK_FAILED');
    }
    const stale = await target.snapshots.orderBy('createdAt').reverse().offset(12).primaryKeys();
    if (stale.length) await target.snapshots.bulkDelete(stale);
  });
}

export async function recoverLatestLocalSnapshot(target = db): Promise<ExportPayload> {
  const snapshots = await target.snapshots.orderBy('createdAt').reverse().limit(12).toArray();
  let payload: ExportPayload | undefined;
  for (const snapshot of snapshots) {
    try {
      const plain = snapshot.payload.startsWith('raw:') ? snapshot.payload.slice(4) : await gunzipBase64(snapshot.payload);
      payload = previewLocalImport(plain); break;
    } catch { /* Preserve invalid originals and examine the next known snapshot. */ }
  }
  if (!payload) throw new Error('LOCAL_SNAPSHOT_NOT_FOUND');
  await applyImportPayload(payload, 'replace', target);
  const health = await inspectLocalData(target);
  if (!health.ok) throw new Error('LOCAL_SNAPSHOT_INVALID');
  return payload;
}

export async function resetLocalUserData(target = db): Promise<void> {
  await target.transaction('rw', [target.profiles, target.shiftTypes, target.shifts, target.customAllowances, target.paychecks, target.workplaces, target.wageTerms, target.snapshots, target.settings, target.outboxEvents], async () => {
    await Promise.all([
      target.profiles.clear(), target.shiftTypes.clear(), target.shifts.clear(), target.customAllowances.clear(),
      target.paychecks.clear(), target.workplaces.clear(), target.wageTerms.clear(), target.snapshots.clear(), target.settings.clear(), target.outboxEvents.clear(),
    ]);
  });
  await initializeLocalData(target);
}

async function gzipBase64(value: string): Promise<string> {
  const stream = new CompressionStream('gzip');
  const bytes = new Uint8Array(await new Response(new Blob([value]).stream().pipeThrough(stream)).arrayBuffer());
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

async function gunzipBase64(value: string): Promise<string> {
  let binary: string;
  try { binary = atob(value); } catch { throw new Error('LOCAL_SNAPSHOT_INVALID'); }
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  try {
    const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
    return await new Response(stream).text();
  } catch { throw new Error('LOCAL_SNAPSHOT_INVALID'); }
}
